import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';
import FileDropzone from './FileDropzone';
import { InputFileImage } from './InputFileImage';

const FormAddVideo = () => {
    const { setNotification } = useNotification();
    const [groups, setGroups] = useState([]);
    const [videoData, setVideoData] = useState({
        nome: '',
        img_preview: '',
        baixavel: false,
        compartilhavel: false,
        notificar: false,
        grupo: '',
        youtube_link: '',
        video_arq: '',
    });

    const [videoErrorData, setVideoErrorData] = useState({
        nome: [],
        img_preview: [],
        baixavel: [],
        compartilhavel: [],
        notificar: [],
        grupo: [],
        youtube_link: [],
        video_arq: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setVideoData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setVideoData((prevData) => ({
            ...prevData,
            [name]: selectedOption.value,
        }));
    };

    const handleImageCrop = (croppedImage, fileExtenstion, name) => {
        setVideoData((prevData) => ({
            ...prevData,
            [name]: croppedImage,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const formData = new FormData();

            const fileFields = ['img_preview', 'video_arq'];

            Object.keys(videoData).forEach((key) => {
                if (!fileFields.includes(key)) {
                    const value = videoData[key];
                    formData.append(key, typeof value === 'boolean' ? (value ? 1 : 0) : value);
                }
            });

            fileFields.forEach((field) => {
                if (videoData[field]) {
                    formData.append(field, videoData[field]);
                }
            });

            if (!videoData.video_arq) {
                throw new Error('Arquivo de vídeo é obrigatório');
            } else {
                const duration = await getVideoDuration(videoData.video_arq);
                formData.append('duracao', Math.round(duration));
            }

            const response = await fetch(`${baseUrl}/manager/materiais/videos/novo/${id}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                body: formData,
            });

            const responseData = await response.json();

            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Vídeo adicionado com sucesso!',
                    show: true,
                });
                navigate(`/manager/produtos/editar/${id}`);
            } else {
                if (response.status === 422) {
                    setVideoErrorData(responseData);
                    throw new Error('Erro ao adicionar o vídeo');
                } else if (response.status === 401) {
                    throw new Error('Sessão expirada. Por favor, faça login novamente.');
                } else {
                    throw new Error(responseData.message || 'Erro ao processar a requisição');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    const getVideoDuration = (file) => {
        return new Promise((resolve, reject) => {
            const videoElement = document.createElement('video');
            videoElement.preload = 'metadata';

            videoElement.onloadedmetadata = () => {
                resolve(videoElement.duration);
            };

            videoElement.onerror = () => {
                reject(new Error('Erro ao carregar o vídeo'));
            };

            videoElement.src = URL.createObjectURL(file);
        });
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await fetch(`${baseUrl}/manager/grupos?tipo=video`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setGroups(
                        data.grupos.map((grupo) => ({
                            value: grupo.id,
                            label: grupo.nome,
                        }))
                    );
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar os grupos.',
                        show: true,
                    });
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro na requisição',
                    show: true,
                });
            }
        };

        fetchContent();
    }, []);

    return (
        <>
            <Link
                to={`/manager/produtos/videos/${id}`}
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Adicionar vídeo</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-1">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={videoData.nome}
                                    onClick={() => setVideoErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {videoErrorData.nome && videoErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-1/3">
                                <label className="block text-sm font-medium mb-1">Grupo</label>
                                <Select
                                    name="grupo"
                                    options={groups}
                                    onClick={() => videoErrorData((prevData) => ({ ...prevData, grupo: [] }))}
                                    onChange={(selectedOption) => handleSelectChange('grupo', selectedOption)}
                                    placeholder="Selecione uma opção..."
                                    classNamePrefix="admin-select"
                                    className="product-select"
                                />
                                {videoErrorData.grupo && videoErrorData.grupo[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.grupo[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, grupo: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/12">
                                <label className="block text-sm font-medium mb-1">Baixável?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="baixavel"
                                        checked={videoData.baixavel}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {videoErrorData.baixavel && videoErrorData.baixavel[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.baixavel[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, baixavel: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/6">
                                <label className="block text-sm font-medium mb-1">Compartilhável?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="compartilhavel"
                                        checked={videoData.compartilhavel}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {videoErrorData.compartilhavel && videoErrorData.compartilhavel[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.compartilhavel[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, compartilhavel: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/6">
                                <label className="block text-sm font-medium mb-1">Notificar usuários?</label>

                                <label className="cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="notificar"
                                        checked={videoData.notificar}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                </label>

                                {videoErrorData.notificar && videoErrorData.notificar[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.notificar[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, notificar: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-1">Link Youtube</label>
                                <input
                                    type="text"
                                    name="youtube_link"
                                    value={videoData.youtube_link}
                                    onClick={() => setVideoErrorData((prevData) => ({ ...prevData, youtube_link: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {videoErrorData.youtube_link && videoErrorData.youtube_link[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.youtube_link[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, youtube_link: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-5/12">
                                <label className="block text-sm font-medium">Imagem prévia</label>

                                <InputFileImage
                                    name="img_preview"
                                    imagem={`/assets/img/admin/image-select.png`}
                                    size={{ largura: '320', altura: '430' }}
                                    allowCrop={true}
                                    onImageCrop={handleImageCrop}
                                />
                                {videoErrorData.img_preview && videoErrorData.img_preview[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.img_preview[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, img_preview: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/3">
                                <label className="block text-sm font-medium">Vídeo</label>

                                <FileDropzone
                                    type="video"
                                    name="video_arq"
                                    value={videoData.video_arq}
                                    onChange={(file) => {
                                        setVideoData((prev) => ({ ...prev, video_arq: file }));
                                        setVideoErrorData((prevData) => ({ ...prevData, video_arq: [] }));
                                    }}
                                    onDelete={() => setVideoData((prev) => ({ ...prev, video_arq: null }))}
                                />

                                {videoErrorData.video_arq && videoErrorData.video_arq[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {videoErrorData.video_arq[0]}{' '}
                                        <span
                                            onClick={() => setVideoErrorData((prevData) => ({ ...prevData, video_arq: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormAddVideo;
